import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const SUBGROUP_OPEN_PERSIST_KEY = 'dobox-subgroup-open';

export const DEFAULT_SUBGROUP_OPEN = true;

interface DoBoxSubgroupOpenState {
  openStatesByTaskGroupId: Record<string, Record<string, boolean>>;
}

interface DoBoxSubgroupOpenActions {
  toggle: (taskGroupId: string, id: string) => void;
  syncSubgroups: (taskGroupId: string, ids: string[]) => void;
  reset: () => void;
}

type DoBoxSubgroupOpenStore = DoBoxSubgroupOpenState & DoBoxSubgroupOpenActions;

const INITIAL_STATE: DoBoxSubgroupOpenState = {
  openStatesByTaskGroupId: {},
};

// localStorage (synchronous, no hydration flash) instead of the IndexedDB adapter,
// whose getItem silently returns null on timeout and this preference has no other source of truth.
export const useDoBoxSubgroupOpenStore = create<DoBoxSubgroupOpenStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      toggle: (taskGroupId: string, id: string) =>
        set((s) => {
          const groupStates = s.openStatesByTaskGroupId[taskGroupId] ?? {};
          return {
            openStatesByTaskGroupId: {
              ...s.openStatesByTaskGroupId,
              [taskGroupId]: {
                ...groupStates,
                [id]: !(groupStates[id] ?? DEFAULT_SUBGROUP_OPEN),
              },
            },
          };
        }),
      syncSubgroups: (taskGroupId: string, ids: string[]) =>
        set((s) => {
          const groupStates = s.openStatesByTaskGroupId[taskGroupId] ?? {};
          const next: Record<string, boolean> = {};
          for (const id of ids) {
            next[id] =
              id in groupStates ? groupStates[id] : DEFAULT_SUBGROUP_OPEN;
          }
          return {
            openStatesByTaskGroupId: {
              ...s.openStatesByTaskGroupId,
              [taskGroupId]: next,
            },
          };
        }),
      reset: () => set(INITIAL_STATE),
    }),
    {
      name: SUBGROUP_OPEN_PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
